import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';

const CartContext = createContext();

const readInitialState = () => {
  if (typeof window === 'undefined') {
    return [];
  }
  try {
    const cached = window.localStorage.getItem('doorimports:cart');
    return cached ? JSON.parse(cached) : [];
  } catch (err) {
    console.warn('Failed to read cart from storage', err);
    return [];
  }
};

export function CartProvider({ children }) {
  const [items, setItems] = useState(readInitialState);

  useEffect(() => {
    try {
      window.localStorage.setItem('doorimports:cart', JSON.stringify(items));
    } catch (err) {
      console.warn('Failed to persist cart', err);
    }
  }, [items]);

  const addItem = (item) =>
    setItems((prev) => {
      const existingIndex = prev.findIndex((p) => p.id === item.id);
      if (existingIndex === -1) {
        return [...prev, { ...item, quantity: 1 }];
      }
      const next = [...prev];
      next[existingIndex] = {
        ...next[existingIndex],
        quantity: next[existingIndex].quantity + 1,
      };
      return next;
    });

  const clearCart = () => setItems([]);

  const total = items.reduce((sum, item) => sum + item.price * item.quantity, 0);
  const count = items.reduce((sum, item) => sum + item.quantity, 0);

  const value = useMemo(
    () => ({
      items,
      addItem,
      clearCart,
      count,
      total,
    }),
    [items, count, total],
  );

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCart() {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
}
